import React from "react";
import { UnitTypeBadge, UnitAddressActions, buildMapsQueryFromUnit } from "./OccupancyUnitMeta";
import { roomPlanningLabel, roomPlanningBadges } from "../utils/unitOccupancyStatus";

/**
 * Maps the canonical `/admin/occupancy/rooms` status to a display key.
 *
 * UNRECOGNISED FAILS CLOSED TO `unknown`, NOT TO `free` (OC1/D2). It used to
 * answer `free` for anything it did not recognise — including a room the backend
 * sent without a status at all — so a gap in the payload arrived on screen as a
 * vacancy somebody could act on. `unknown` is the same device
 * `CoLivingOccupancyCell` uses for a month it could not read: its own word, its
 * own neutral tone, and it can never pass as free.
 *
 * `Room.status` is NOT an input here any more. It is a legacy column
 * (`db/models.py:746-747`), written by hand through `POST/PATCH /admin/rooms` and
 * derived from nothing; reading it was D2.
 */
function normalizeOccupancyStatusKey(raw) {
  const s = String(raw || "").toLowerCase().trim();
  if (s === "belegt" || s === "occupied") return "occupied";
  if (s === "reserviert" || s === "reserved") return "reserved";
  if (s === "frei" || s === "free") return "free";
  return "unknown";
}

/**
 * The tiles to render, or `null` when the canonical answer is not available.
 *
 * THREE OUTCOMES, AND THEY MUST STAY DISTINGUISHABLE:
 *   `[]`    this unit has no rooms recorded at all
 *   `null`  it has rooms, but the canonical occupancy is not available
 *   rows    the canonical answer
 *
 * `null` is where the legacy fallback used to be. It built a tile per room out of
 * `Room.status`, so a fully let unit whose column was never maintained was
 * painted entirely red under a heading that says "aus Tenancies". There is no
 * honest guess to make here: the only source of current occupancy is the tenancy
 * truth, and if it did not arrive the panel says so.
 *
 * `rooms` is still used, and only for what it can answer: whether this unit has
 * any rooms at all. Which rooms COUNT is a population question OC1 does not
 * touch (its §16), so that call is unchanged.
 */
function getRoomDisplayList(unit, rooms = [], occupancyData = null) {
  const unitId = unit?.id ?? unit?.unitId ?? "";
  const roomList = Array.isArray(rooms)
    ? rooms.filter((r) => (r.unitId ?? r.unit_id) === unitId)
    : [];

  if (roomList.length === 0) return [];

  if (occupancyData && Array.isArray(occupancyData.rooms) && occupancyData.rooms.length > 0) {
    return occupancyData.rooms.map((occ) => ({
      room_id: occ.room_id,
      room_name: occ.room_name ?? occ.room_id,
      // Handed on verbatim; `normalizeOccupancyStatusKey` is the one place that
      // decides what an absent or unrecognised value means, and it no longer
      // decides "free".
      status: occ.status ?? null,
      tenant_name: occ.tenant_name ?? null,
      rent: occ.rent ?? occ.price ?? null,
      // Additive planning fields (room-availability sprint). planning_state is the
      // backend source of truth; roomPlanningLabel only maps it to display copy.
      planning_state: occ.planning_state ?? null,
      free_from_date: occ.free_from_date ?? null,
      effective_occupied_until: occ.effective_occupied_until ?? null,
      next_tenant_name: occ.next_tenant_name ?? null,
      next_move_in_date: occ.next_move_in_date ?? null,
      is_reserved_future: occ.is_reserved_future ?? false,
    }));
  }

  return null;
}

// THE FALLTHROUGH IS NEUTRAL, NOT RED (OC1/D2). All three of these used to end
// on the `free` styling, so a state nobody could read borrowed the vacancy
// colour — the same mistake `CoLivingOccupancyCell` describes at its :17-21 and
// fixes the same way: `free` becomes explicit, and what is left over gets the
// muted tone and its own word. The three known states keep their existing
// colours byte for byte; harmonising the occupancy palette across surfaces is a
// separate, later slice (OC4).
function roomCardClasses(status) {
  if (status === "occupied") {
    return "border-[rgba(61,220,132,0.2)] bg-[rgba(61,220,132,0.05)]";
  }
  if (status === "reserved") {
    return "border-[rgba(245,166,35,0.18)] bg-[rgba(245,166,35,0.05)]";
  }
  if (status === "free") {
    return "border-[rgba(255,95,109,0.18)] bg-[rgba(255,95,109,0.05)]";
  }
  // UNKNOWN IS THE ONLY TONE HERE THAT IS NOT A TINT (C3). The three above are
  // translucent rgba over the panel surface, and that surface IS remapped for
  // light mode — which is why they survive the theme. This branch used an OPAQUE
  // `bg-[#191c28]`, and `index.css` has no GLOBAL light-mode override for it:
  // the only rules are scoped to `[data-admin-page="belegung"]` (index.css:614)
  // and `AdminUnitDetailPage` sets no such attribute. The room name's
  // `text-[#edf0f7]` IS remapped globally (index.css:336), so the text turned
  // dark while the surface stayed dark — 1.03:1, measured.
  //
  // Semantic tokens instead, so both themes are answered by the token and not by
  // a page-scoped patch. SEMANTICS UNCHANGED (C3 §3/§5): still the neutral
  // chrome, still labelled "Unbekannt", no colour meaning introduced.
  return "border-border bg-card";
}

function roomDotClass(status) {
  if (status === "occupied") return "bg-[#3ddc84]";
  if (status === "reserved") return "bg-[#f5a623]";
  if (status === "free") return "bg-[#ff5f6d]";
  return "bg-[#4a5070]";
}

// "Unbekannt" is the word `lifecycleCalendarTruth.LIFECYCLE_LABELS` already uses
// for a state this layer could not read — taken from there rather than invented.
function roomLabelDe(status) {
  if (status === "occupied") return "Belegt";
  if (status === "reserved") return "Reserviert";
  if (status === "free") return "Frei";
  return "Unbekannt";
}

/** Compact inline pill matching the existing dark header-pill style. */
function Pill({ className = "", children }) {
  return (
    <span
      className={`inline-flex items-center rounded-full border px-2 py-[2px] text-[9px] font-semibold ${className}`}
    >
      {children}
    </span>
  );
}

// Primary status pill tone (Belegt = green, Reserviert = calm amber, Frei = red,
// Unbekannt = the panel's own muted chrome so it cannot be mistaken for any of
// the three answers).
const PRIMARY_PILL_CLASS = {
  occupied: "border-[rgba(61,220,132,0.25)] bg-[rgba(61,220,132,0.1)] text-[#3ddc84]",
  reserved: "border-[rgba(245,166,35,0.25)] bg-[rgba(245,166,35,0.1)] text-[#f5a623]",
  free: "border-[rgba(255,95,109,0.22)] bg-[rgba(255,95,109,0.08)] text-[#ff5f6d]",
  // Same opaque-hex problem as the tile, and worse: the belegung-scoped rule
  // repaints the TILE, never the pill inside it, so this one failed in light on
  // BOTH pages (2.61:1). It now carries the repo's established neutral chip
  // contract — byte-identical to the Owner/Property occupancy pill (B2) — which
  // measures 16.33:1 light and 16.47:1 dark.
  //
  // The chip's SEPARATION from the tile is deliberately soft and is not a
  // contrast claim: fill against fill is 1.09:1 light / 1.10:1 dark and the
  // `border-border` outline 2.08:1 / 1.57:1. SC 1.4.11 does not bind here — the
  // word "Unbekannt" carries the state and the tile is not interactive — so the
  // outline only has to delineate, which at this size it does. The numbers are
  // written down so the next reader does not mistake the sentence for a gate.
  unknown: "border-border bg-muted text-foreground",
};

function RoomTile({ room }) {
  const statusKey = normalizeOccupancyStatusKey(room.status);
  const rentStr =
    room.rent != null
      ? `CHF ${Number(room.rent).toLocaleString("de-CH", { maximumFractionDigits: 0 })}`
      : null;
  const planningLine = roomPlanningLabel(room);
  const { endingSoon, hasSuccessor } = roomPlanningBadges(room);

  return (
    <div
      className={`occupancy-map-room-tile flex min-w-[150px] flex-1 flex-col rounded-[9px] border p-[12px_14px] ${roomCardClasses(
        statusKey
      )} ${endingSoon ? "border-l-2 border-l-[#ff7a45]" : ""}`}
    >
      <p className="mb-[8px] flex items-center gap-[6px] text-[12px] font-medium text-[#edf0f7]">
        <span className={`h-[6px] w-[6px] shrink-0 rounded-full ${roomDotClass(statusKey)}`} />
        {room.room_name}
      </p>
      {/* One primary status badge (replaces the old "Status:" text row) + optional
          secondary badges. Reserviert stays calm amber; Endet bald is a stronger
          orange-red so the two never look alike. */}
      <div className="mb-[6px] flex flex-wrap gap-[4px]">
        <Pill className={PRIMARY_PILL_CLASS[statusKey] || PRIMARY_PILL_CLASS.unknown}>
          {roomLabelDe(statusKey)}
        </Pill>
        {endingSoon ? (
          <Pill className="border-[rgba(255,122,69,0.35)] bg-[rgba(255,122,69,0.15)] text-[#ff7a45]">
            Endet bald
          </Pill>
        ) : null}
        {hasSuccessor ? (
          <Pill className="border-[rgba(155,124,244,0.3)] bg-[rgba(155,124,244,0.12)] text-[#9d7cf4]">
            Nachmieter vorhanden
          </Pill>
        ) : null}
      </div>
      {room.tenant_name ? (
        <div className="mb-[3px] text-[11px] text-[#8892b0]">{room.tenant_name}</div>
      ) : (
        <div className="mb-[3px] text-[11px] italic text-[#4a5070]">—</div>
      )}
      {rentStr ? (
        <div className="text-[11px] text-[#8892b0]">
          {rentStr} / Monat
        </div>
      ) : null}
      {planningLine ? (
        <div className="mt-[4px] text-[10px] text-[#7f8daa]">{planningLine}</div>
      ) : null}
    </div>
  );
}

export default function OccupancyMap({ unit, rooms = [], occupancyData = null, loading = false }) {
  const displayList = getRoomDisplayList(unit, rooms, occupancyData);
  const unitLabel = unit?.title ?? unit?.place ?? unit?.unitId ?? unit?.id ?? "Unit";
  const unitIdStr = unit?.unitId ?? unit?.id ?? "";
  const addressFallback =
    buildMapsQueryFromUnit(unit) || String(unit?.address ?? "").trim() || unit?.place || "—";

  // `null` means the canonical answer is missing, so there is nothing to count
  // yet. Reading `.length` off it would throw, which is why the three states are
  // decided before any of these figures are taken.
  const rows = displayList ?? [];
  const totalRooms = rows.length;
  const occupiedCount = rows.filter(
    (r) => normalizeOccupancyStatusKey(r.status) === "occupied"
  ).length;
  const reservedCount = rows.filter(
    (r) => normalizeOccupancyStatusKey(r.status) === "reserved"
  ).length;

  // LOADING COMES FIRST, AND IT IS A DIFFERENT STATEMENT FROM UNAVAILABLE
  // (OC1 §8). Both parents leave `occupancyData` at `null` until the response
  // lands, so without this branch the first paint of every mount would claim the
  // data could not be had.
  if (loading) {
    return (
      <div
        className="mx-[14px] my-[12px] rounded-[10px] border border-border bg-card px-8 py-10 text-center text-[13px] text-muted-foreground"
        data-occupancy-loading
      >
        <p className="m-0 font-semibold">Raumstatus wird geladen…</p>
      </div>
    );
  }

  // Rooms exist, the canonical occupancy does not. NO GUESS IS OFFERED: the
  // legacy `Room.status` column that used to fill this gap is a hand-maintained
  // field, and painting a let unit red from it was worse than saying nothing
  // (OC1/D2). The wording follows the repo's own "<Ding> nicht verfügbar"
  // pattern; the second line says what it does not mean, the way
  // `AdminCoLivingDashboardPage`'s operating note does.
  if (displayList === null) {
    return (
      <div
        className="mx-[14px] my-[12px] rounded-[10px] border border-border bg-card px-8 py-10 text-center"
        data-occupancy-unavailable
      >
        <p className="m-0 text-[13px] font-semibold text-foreground">
          Belegungsdaten nicht verfügbar
        </p>
        <p className="mt-2 text-[12px] text-muted-foreground">
          Der Raumstatus wird aus den Mietverhältnissen abgeleitet und nicht geschätzt.
        </p>
      </div>
    );
  }

  if (!displayList.length) {
    return (
      <div className="mx-[14px] my-[12px] rounded-[10px] border border-[#1c2035] bg-[#141720] px-8 py-10 text-center">
        <p className="m-0 text-[13px] font-semibold text-[#edf0f7]">Keine Rooms für diese Unit vorhanden.</p>
        <p className="mt-2 text-[12px] text-[#4a5070]">
          Räume unter Objekte anlegen, um die Belegung anzuzeigen.
        </p>
      </div>
    );
  }

  return (
    <div className="mx-[14px] my-[12px] overflow-hidden rounded-[10px] border border-[#1c2035] bg-[#141720]">
      <div className="occupancy-map-unit-header flex flex-wrap items-center gap-[10px] border-b border-[#1c2035] px-[14px] py-[11px]">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            {unit ? <UnitTypeBadge unit={unit} /> : null}
            <p className="text-[12px] font-medium text-[#edf0f7]">{unitLabel}</p>
          </div>
          <div className="mt-[6px]">
            <UnitAddressActions
              unit={unit}
              fallbackUnitId={unitIdStr}
              displayText={addressFallback}
              linkClassName="text-[12px] font-medium text-[#5b9cf6]"
            />
          </div>
          <p className="mt-[4px] text-[10px] text-[#4a5070]">
            {unitIdStr ? `ID ${unitIdStr}` : "Raumstatus (Belegt / Reserviert / Frei)"}
          </p>
        </div>
        <div className="ml-auto flex flex-wrap gap-[6px]">
          <span className="occupancy-room-count-badge rounded-full border border-[#1c2035] bg-[#191c28] px-2 py-[2px] text-[9px] font-semibold text-[#8892b0]">
            {totalRooms} Rooms
          </span>
          <span className="rounded-full border border-[rgba(61,220,132,0.2)] bg-[rgba(61,220,132,0.1)] px-2 py-[2px] text-[9px] font-semibold text-[#3ddc84]">
            {occupiedCount} Belegt
          </span>
          <span className="rounded-full border border-[rgba(245,166,35,0.2)] bg-[rgba(245,166,35,0.1)] px-2 py-[2px] text-[9px] font-semibold text-[#f5a623]">
            {reservedCount} Reserviert
          </span>
        </div>
      </div>
      <div className="flex flex-wrap gap-[8px] px-[12px] py-[12px]">
        {displayList.map((room) => (
          <RoomTile key={room.room_id} room={room} />
        ))}
      </div>
    </div>
  );
}
